import { ChatChannelResponse } from '@/lib/api';

const MESSAGE_PREVIEW_MAX_LENGTH = 50;

interface ChannelListItemProps {
  channel: ChatChannelResponse;
  isActive: boolean;
  onClick: () => void;
}

export function ChannelListItem({ channel, isActive, onClick }: ChannelListItemProps) {
  const displayName =
    channel.channelType === 'direct'
      ? channel.dmPartnerDisplayName || channel.name || channel.dmPartnerId || 'Direct Message'
      : channel.name || 'Unnamed Channel';

  const lastMessagePreview = channel.lastMessage
    ? channel.lastMessage.content.substring(0, MESSAGE_PREVIEW_MAX_LENGTH) +
      (channel.lastMessage.content.length > MESSAGE_PREVIEW_MAX_LENGTH ? '...' : '')
    : null;

  const lastMessageTime = channel.lastMessage
    ? formatRelativeTime(channel.lastMessage.createdAt)
    : null;

  const unread = channel.unreadCount ?? 0;

  const avatarContent =
    channel.channelType === 'global'
      ? '#'
      : channel.channelType === 'direct'
        ? displayName.charAt(0).toUpperCase()
        : '#';

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50 ${
        isActive ? 'bg-muted' : ''
      }`}
    >
      <div
        className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          unread > 0 ? 'bg-primary' : 'bg-primary/10'
        }`}
      >
        <span
          className={`text-sm font-medium ${unread > 0 ? 'text-primary-foreground' : 'text-primary'}`}
        >
          {unread > 0 ? unread : avatarContent}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium truncate text-foreground">{displayName}</span>
          {lastMessageTime && (
            <span className="text-xs text-muted-foreground flex-shrink-0 ml-2">
              {lastMessageTime}
            </span>
          )}
        </div>
        {lastMessagePreview && (
          <p className="text-xs text-muted-foreground truncate mt-0.5">{lastMessagePreview}</p>
        )}
      </div>
    </button>
  );
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'now';
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
